import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faShieldAlt,
  faChartBar,
  faLightbulb,
  faArrowRight,
  faHeartbeat,
} from "@fortawesome/free-solid-svg-icons";

const features = [
  {
    icon: faChartLine,
    title: "Evidence-Based Predictions",
    description:
      "Built on the internationally validated PREDICT algorithm, incorporating the latest clinical research and outcomes data.",
    color: "text-blue-600",
    bg: "bg-blue-50",
  },
  {
    icon: faShieldAlt,
    title: "HIPAA Compliant",
    description:
      "Enterprise-grade security with end-to-end encryption, audit logging, and full HIPAA compliance for patient data protection.",
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  {
    icon: faHeartbeat,
    title: "Patient-Centered Care",
    description:
      "Designed for oncologists, surgeons, and healthcare teams with comprehensive treatment recommendations and guidelines.",
    color: "text-rose-600",
    bg: "bg-rose-50",
  },
  {
    icon: faChartBar,
    title: "Continuous Learning",
    description:
      "AI-powered system that continuously improves predictions based on real-world outcomes and clinical feedback.",
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  {
    icon: faLightbulb,
    title: "Intelligent Insights",
    description:
      "Advanced analytics provide actionable insights for personalized treatment planning and risk stratification.",
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  {
    icon: faArrowRight,
    title: "Seamless Integration",
    description:
      "Easy integration with existing clinical workflows and electronic health record systems.",
    color: "text-purple-600",
    bg: "bg-purple-50",
  },
];

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-slate-100 py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
            <FontAwesomeIcon icon={faHeartbeat} />
            PREDICT Algorithm v3.0
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-4 leading-tight">
            Advanced Breast Cancer
            <span className="block text-blue-600 mt-1">Risk Prediction</span>
          </h1>
          <p className="text-xl text-slate-500 mb-8 max-w-2xl mx-auto leading-relaxed">
            Empowering healthcare professionals with evidence-based risk
            assessment and personalized treatment recommendations using the
            latest clinical research.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link
              to="/assessment"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
            >
              Start Assessment
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-700 font-semibold rounded-xl hover:bg-slate-50 border border-slate-200 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              Why Choose Boobalyz?
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              Built on the proven PREDICT algorithm with modern enhancements for
              contemporary clinical practice.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-slate-100 p-6 hover:shadow-md hover:-translate-y-1 transition-all duration-200"
              >
                <div
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${feature.bg} ${feature.color} mb-4`}
                >
                  <FontAwesomeIcon icon={feature.icon} className="text-xl" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Ready to Get Started?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Join healthcare professionals worldwide who trust Boobalyz for
            accurate risk assessment.
          </p>
          <Link
            to="/assessment"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-colors"
          >
            Start Your First Assessment
            <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
